import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { SettingsProvider } from '@services/settings/settings';
import {
	IntegrationsStatus,
	XeroConnections,
	XeroConnectPayload
} from '@models/models';

@Injectable()
export class IntegrationsDataProvider {
	private INTEGRATION_STATUS_URL = '/api/user/integrations';
	private INTEGRATION_STATUS_URL_V1 = '/api/user/v1/integrations';

	private XERO_SETTINGS_URL = '/api/user/xero/settings';
	private XERO_AUTH_URL = '/api/user/xero/authenticate';
	private XERO_CALLBACK_URL = '/api/user/xero/callback';
	private XERO_AUTHORIZED_CONNECTIONS =
		'/api/user/v1/xero/authorised-connections';

	private QUICKBOOKS_AUTH_URL = '/api/user/qb/connect';
	private QUICKBOOKS_CALLBACK_URL = '/api/user/qb/callback';
	private QUICKBOOKS_SETTINGS_URL = '/api/user/qb/settings';

	private SHOOTPROOF_AUTH_URL = '/api/user/shootproof/authUrl';
	private SHOOTPROOF_SETTINGS_URL = '/api/user/shootproof/settings';
	private SHOOTPROOF_EXISTING_SETTINGS_URL =
		'/api/user/shootproof/settings/existing';
	private SHOOTPROOF_CREATE_GALLERY_URL =
		'/api/user/shootproof/galleries/create';
	private SHOOTPROOF_SELECT_GALLERY_URL =
		'/api/user/shootproof/galleries/attach';

	constructor(
		private http: HttpClient,
		private settingsProvider: SettingsProvider
	) {}

	getIntegrationsV1(): Observable<IntegrationsStatus> {
		return this.http.get(this.INTEGRATION_STATUS_URL_V1) as Observable<
			IntegrationsStatus
		>;
	}

	getXeroAuthorizedConnections(url: string): Observable<XeroConnections> {
		return this.http.get(url || this.XERO_AUTHORIZED_CONNECTIONS) as Observable<
			XeroConnections
		>;
	}

	connectXeroV2(url: string, payload: XeroConnectPayload): Observable<Object> {
		return this.http.post(url, payload);
	}

	disconnectXeroV2(url: string): Observable<Object> {
		return this.http.post(url, {});
	}

	// XERO

	getIntegrationOrganization(): Observable<any> {
		return this.http.get(this.INTEGRATION_STATUS_URL);
	}

	initXeroIntegration(): Observable<any> {
		return this.http.get(this.XERO_AUTH_URL);
	}

	editXeroSettings(): Observable<any> {
		return this.http.get(this.XERO_SETTINGS_URL);
	}

	editXeroSettingsById(data): Observable<any> {
		return this.http.put(this.XERO_SETTINGS_URL, data);
	}

	createXeroSettings(data): Observable<any> {
		return this.http.post(this.XERO_SETTINGS_URL, data);
	}

	disconnectXero(): Observable<any> {
		return this.http.delete(this.XERO_SETTINGS_URL);
	}

	sendXeroToken(token): Observable<any> {
		let params = new HttpParams();

		Object.keys(token).forEach(key => {
			params = params.append(key, token[key]);
		});

		return this.http.get(this.XERO_CALLBACK_URL, { params });
	}

	copyInvoiceToXero(id): Observable<any> {
		const url = `/api/user/xero/invoices/${id}/copyToXero`;
		return this.http.put(url, {});
	}

	// QUICKBOOKS

	initQuickbooksIntegration(): Observable<any> {
		return this.http.get(this.QUICKBOOKS_AUTH_URL);
	}

	disconnectQuickbooks(): Observable<any> {
		return this.http.delete(this.QUICKBOOKS_SETTINGS_URL);
	}

	sendQuickbooksToken(params): Observable<any> {
		const parameters = new HttpParams()
			.set('code', params.code)
			.set('state', params.state)
			.set('realmId', params.realmId);
		return this.http.get(this.QUICKBOOKS_CALLBACK_URL, { params: parameters });
	}

	editQuickBooksSettings(): Observable<any> {
		return this.http.get(this.QUICKBOOKS_SETTINGS_URL);
	}

	saveQuickbooksSettings(isSettingsToUpdate: boolean, data): Observable<any> {
		const method = isSettingsToUpdate ? 'put' : 'post';
		return this.http[method](this.QUICKBOOKS_SETTINGS_URL, data);
	}

	copyInvoiceToQuickbooks(id): Observable<any> {
		const url = `/api/user/qb/invoices/${id}`;
		return this.http.put(url, {});
	}

	//    SHOOTPROOF

	initShootProofIntegration(): Observable<any> {
		return this.http.get(this.SHOOTPROOF_AUTH_URL);
	}

	disconnectShootProof(): Observable<any> {
		return this.http.delete(this.SHOOTPROOF_SETTINGS_URL);
	}

	setShootProofSettings(
		method: string,
		text: string,
		code: string
	): Observable<any> {
		const data: any = {};
		if (code) {
			data.code = code;
		}
		if (text) {
			data.clientPortalText = text;
		}
		return this.http[method](this.SHOOTPROOF_SETTINGS_URL, data);
	}

	getShootProofSettings(): Observable<any> {
		return this.http.get(this.SHOOTPROOF_SETTINGS_URL);
	}

	getExistingSpSettings(): Observable<any> {
		return this.http.get(this.SHOOTPROOF_EXISTING_SETTINGS_URL);
	}

	createNewSpGallery(data): Observable<any> {
		return this.http.post(this.SHOOTPROOF_CREATE_GALLERY_URL, data);
	}

	getDefaultsForBrand(selectedBrand): Observable<any> {
		const url = `/api/user/shootproof/brands/${selectedBrand.key}/defaults`;
		return this.http.get(url);
	}

	getGalleriesForBrand(selectedBrand): Observable<any> {
		const url = `/api/user/shootproof/galleries/list?brandId=${
			selectedBrand.key
		}`;
		return this.http.get(url);
	}

	selectExistingSpGallery(data): Observable<any> {
		return this.http.post(this.SHOOTPROOF_SELECT_GALLERY_URL, data);
	}

	deleteShootProofGalleryFromSN(id: number): Observable<any> {
		const url = `/api/user/shootproof/galleries/${id}/snonly`;
		return this.http.delete(url);
	}

	deleteShootProofGalleryFromBoth(id: number): Observable<any> {
		const url = `/api/user/shootproof/galleries/${id}/includingsp`;
		return this.http.delete(url);
	}

	// Fundy
	updateFundyIntegrationState(state: boolean): Observable<any> {
		return this.settingsProvider.updateFundyIntegration(state);
	}

	// ProSelect
	updateProSelectIntegrationState(state: boolean): Observable<any> {
		return this.settingsProvider.updateProSelectIntegration(state);
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=os2z8rc99lf!./shared/services/integrations/integrations.data.service.ts